import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, ArrowRight, Check, Target, User } from 'lucide-react';
import { homeContent, siteConfig } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';

export default function Forside() {
  const { hero, features, whyChooseUs } = homeContent;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative overflow-hidden bg-white">
      <SEO 
        title="Mindset Coaching & Personlig Udvikling | SECoaching"
        description="Vil du bryde begrænsende mønstre og nå dine mål? Få et skræddersyet mindsetforløb hos SECoaching v/ Steffen. Book en uforpligtende samtale i dag!"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-blue-950 text-white overflow-hidden">
        {/* Background Image & Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={siteConfig.images.hero}
            alt="Mental klarhed og personlig udvikling"
            className="h-full w-full object-cover object-right transition-transform duration-300 ease-out"
            style={{
              transform: `scale(${1.05 + scrollY * 0.0005})`,
            }}
          />
          {/* Gradient overlay: Dark on the left for text readability, fading to very light on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/40 via-transparent to-blue-950/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <ScrollReveal direction="up" duration={1000} className="max-w-2xl text-left space-y-6">
            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl leading-tight">
              Skab et stærkere mindset & tag næste skridt mod dine mål
            </h1>
            
            <p className="text-base md:text-lg text-slate-100 leading-relaxed">
              {hero.text1}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-none bg-sky-400 px-6 py-3.5 text-base font-bold text-blue-950 hover:bg-sky-300 hover:-translate-y-1 active:scale-98 transition-all duration-300"
              >
                {hero.cta}
                <ArrowRight className="h-4 w-4 text-blue-950" />
              </Link>
              <Link
                to="/forloeb-og-priser"
                className="inline-flex items-center justify-center rounded-none border border-white bg-transparent px-6 py-3.5 text-base font-semibold text-white hover:border-white/80 hover:-translate-y-1 transition-all duration-300"
              >
                Se forløb & priser
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up" duration={1000} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Text */}
            <div className="lg:col-span-5 space-y-4">
              <h2 className="text-3xl font-bold tracking-tight text-blue-950 sm:text-4xl">
                Få redskaberne til at skabe varig forandring
              </h2>
              <p className="text-lg text-slate-600 mt-4">
                Gennem personlig coaching arbejder vi med de fundamentale elementer i dit mindset for at frigøre dit fulde potentiale.
              </p>
            </div>

            {/* Right Column: Points */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                {features.map((feature, idx) => {
                  let borderClasses = "";
                  if (idx === 0) {
                    borderClasses = "border-b sm:border-b-0 sm:border-r border-slate-200/80 sm:pr-10 pb-8 sm:pb-0";
                  } else if (idx === 1) {
                    borderClasses = "border-b sm:border-b-0 border-slate-200/80 sm:pl-10 pb-8 sm:pb-0";
                  } else if (idx === 2) {
                    borderClasses = "sm:col-span-2 sm:border-t border-slate-200/80 sm:pt-10 pt-2";
                  }
                  return (
                    <div 
                      key={idx} 
                      className={`flex flex-col ${borderClasses}`}
                    >
                      <div className="mb-4 text-slate-900">
                        {idx === 0 && <Target className="h-8 w-8" />}
                        {idx === 1 && <Brain className="h-8 w-8" />}
                        {idx === 2 && <User className="h-8 w-8" />}
                      </div>
                      <h3 className="text-xl font-bold text-blue-950 mb-3">{feature.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-slate-50 py-6 border-b border-slate-100 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up" duration={1000} className="grid grid-cols-1 md:grid-cols-12 items-center gap-12 lg:gap-16">
            {/* Image */}
            <div className="md:col-span-6 lg:col-span-5 w-full flex items-center justify-center py-6">
              <img
                src={siteConfig.images.portrait}
                alt="Steffen - Mindsetcoach"
                className="w-full h-auto max-h-[500px] object-cover rounded-none shadow-md"
              />
            </div>

            {/* Text */}
            <div className="space-y-8 md:col-span-6 lg:col-span-7 py-6">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold tracking-tight text-blue-950 sm:text-4xl">
                  {whyChooseUs.title}
                </h2>
                <p className="text-lg text-slate-600 mt-2">
                  Hos SECoaching får du ikke bare en samtalepartner, men en dedikeret coach, der støtter dig i at omsætte tanker til konkrete handlinger.
                </p>
              </div>

              <div className="space-y-3">
                {whyChooseUs.points.map((point, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-slate-900 mt-1">
                      <Check className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-blue-950">{point.title}</h4>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Link
                  to="/om-mig"
                  className="inline-flex items-center gap-2 text-sm font-bold text-blue-900 hover:text-sky-500 transition-colors"
                >
                  Læs mere om min baggrund og vision
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-950 text-white py-16 relative overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-35 transition-transform duration-500 hover:scale-105"
          style={{ backgroundImage: `url('${siteConfig.images.mindset}')` }}
        />
        {/* Background Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/75 via-blue-950/85 to-blue-950" />
        </div>
        
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center relative z-20">
          <ScrollReveal direction="up" duration={1000} className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Er du klar til at tage næste skridt?
            </h2>
            
            <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Book en uforpligtende samtale, hvor vi kan drøfte dine mål, og hvordan et personligt forløb kan hjælpe dig videre.
            </p>

            <div className="pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-none bg-sky-400 px-8 py-4 text-base font-bold text-blue-950 hover:bg-sky-300 hover:-translate-y-1 active:scale-98 transition-all duration-300"
              >
                Book en samtale nu
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
